<template>
  <div class="card log-viewer-container">
    <div class="card-header d-flex justify-content-between align-items-center">
      <div class="button-panel d-flex align-items-center">
        <button class="btn btn-secondary btn-with-icon mono" @click="reload"><i class="icon mdp-Refresh"></i> {{ $t("contextMenu.refresh") }}</button>
        <button class="btn btn-secondary btn-with-icon mono" :disabled="!filteredLogs.length" @click.prevent="download">
          <i class="icon mdp-Download"></i> {{ $t("download") }}
        </button>
        <div class="d-flex align-items-center">
          <span class="mr-2 flex-shrink-0">{{ $t("LogCurrentLevel") }}: </span>
          <b-dropdown :text="currentLogLevel" size="sm" toggle-class="mono text-uppercase dropdown-toggle-group">
            <b-dropdown-item @click.prevent="logLevel = 0">{{ $t("log.Information") }}</b-dropdown-item>
            <b-dropdown-item @click.prevent="logLevel = 1">{{ $t("log.Warning") }}</b-dropdown-item>
            <b-dropdown-item @click.prevent="logLevel = 2">{{ $t("log.Error") }}</b-dropdown-item>
          </b-dropdown>

          <div v-if="showSuccess" class="ml-2 badge bg-success">
            {{ $t("LogLevelUpdated") }}
          </div>
        </div>
      </div>
      <div class="d-flex flex-row status-group align-items-center">
        <div class="mr-2">
          <dropdown-menu
            button-class="mono"
            :items="viewMenuItems"
            :text="$t('View')"
            icon="mdp-ViewList"
            data-cy="api-log-viewer-view-menu"
            right></dropdown-menu>
        </div>
        <div>
          <MDatePicker v-model="date" />
        </div>
      </div>
    </div>
    <div class="card-body log-viewer-fixed-height">
      <table class="table table-stripped log-viewer" v-if="!rawView">
        <thead>
          <tr>
            <th>{{ $t("AttributeDataTypes.Date") }}</th>
            <th>{{ $t("statuspage.log.severity") }}</th>
            <th class="log-message">{{ $t("statuspage.log.message") }}</th>
            <th class="log-arg">{{ $t("Arguments") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(log, index) in pagedRecords" :key="index" v-safe-class="getClasses(log.severity)">
            <td>
              {{ this.$d(log.eventTime, "shortDateTime") }}
            </td>
            <td>
              {{ log.severity }}
            </td>
            <td>
              <div>{{ log.message }}</div>
            </td>
            <td>
              <json-viewer v-if="log.metadata" :value="log.metadata" :expanded="false" :expand-depth="0"></json-viewer>
              <div v-else>-</div>
            </td>
          </tr>
          <tr v-if="!pagedRecords.length">
            <td colspan="3" class="text-italic">
              {{ $t("log.NoEntries") }}.
              <span v-if="this.logs.length != this.filteredLogs.length">({{ $t("log.FiltersApplied") }})</span>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="raw-logs-display" v-else>
        {{ rawLogs }}
      </div>
    </div>
    <div class="card-footer" v-if="!rawView">
      <div class="d-flex justify-content-center">
        <div class="d-flex flex-row align-items-center">
          <div class="input-group log-paging-group">
            <div class="input-group-prepend" v-if="page > 1">
              <button class="btn btn-secondary" v-tooltip="$t('LogPreviousPage')" @click.prevent="inputPage = page - 1">
                <Icon core-ui icon="caret-left"></Icon>
              </button>
            </div>
            <input v-if="totalPages > 1" class="form-control log-viewer-page-input" v-model.lazy="inputPage" @keyup.enter="inputPage = $event.target.value" />
            <span v-if="totalPages > 1 && page < totalPages">/</span>
            <input v-if="totalPages > 1" class="form-control log-viewer-page-input" v-model.lazy="totalPages" disabled />
            <div class="input-group-append" v-if="page < totalPages">
              <button class="btn btn-secondary" v-tooltip="$t('LogNextPage')" @click.prevent="inputPage = page + 1">
                <Icon core-ui icon="caret-right"></Icon>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MDatePicker from "@/BasicComponentLibrary/MDatePicker.vue";
import Icon from "@common/components/Icon.vue";
import JsonViewer from "vue-json-viewer";
import { mapState } from "vuex";
import eventLogger from "../../services/ApiLogger/EventLogger";
import endpoints from "../../services/endpoints";
import DropdownMenu from "@common/components/MenuBar/DropdownMenu.vue";
import moment from "moment";
import { useFiles } from "@common/composable/useFiles";

export default {
  setup() {
    const { triggerFileDownloadViaEndpoint } = useFiles();

    return { triggerFileDownloadViaEndpoint };
  },
  data() {
    return {
      search: "",
      logs: [],
      date: new Date(),
      rawView: false,
      rawLogs: "",
      inputPage: 1,
      pagesize: 10,
      logLevel: eventLogger.loglevel,
      selectedLevels: ["info", "warn", "error"],
      showSuccess: false,
      viewMenuItems: [
        {
          text: this.$t("exports.log.Information"),
          changeHandler: (nv) => this.updateViewOptions("info", nv),
          type: 1,
          group: "logs",
          checked: true
        },
        {
          text: this.$t("exports.log.Warning"),
          changeHandler: (nv) => this.updateViewOptions("warn", nv),
          type: 1,
          group: "logs",
          checked: true
        },
        {
          text: this.$t("exports.log.Error"),
          changeHandler: (nv) => this.updateViewOptions("error", nv),
          type: 1,
          group: "logs",
          checked: true
        }
      ]
    };
  },
  computed: {
    ...mapState(["meta", "auth", "userSettings"]),
    filteredLogs() {
      return this.logs.filter((entry) => this.selectedLevels.includes(entry.severity?.toLowerCase()));
    },
    page() {
      return parseInt(this.inputPage) || 1;
    },
    pagedRecords() {
      const start = (this.page - 1) * this.pagesize;
      const end = Math.min(this.filteredLogs.length, start + this.pagesize);
      return this.filteredLogs.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredLogs.length / this.pagesize);
    },
    levels: function () {
      return [
        { text: this.$t("log.Information"), value: "info" },
        { text: this.$t("log.Warning"), value: "warn" },
        { text: this.$t("log.Error"), value: "error" }
      ];
    },
    currentLogLevel: function () {
      switch (this.logLevel) {
        case 0:
          return this.$t("log.Information");
        case 1:
          return this.$t("log.Warning");
        case 2:
          return this.$t("log.Error");
      }

      return "";
    }
  },
  components: {
    JsonViewer,
    Icon,
    MDatePicker,
    DropdownMenu
  },
  methods: {
    getClasses(severity) {
      return "log-" + severity.toLowerCase();
    },
    download() {
      const date = moment(this.date).toISOString();
      triggerFileDownloadViaEndpoint(endpoints.logging.export, {
        date: date
      });
    },
    reload: async function () {
      const logs = await eventLogger.GetLogsFor(this.date);
      this.logs = logs;
    },
    updateViewOptions: function (option, add) {
      const index = this.selectedLevels.indexOf(option);
      if (!add && index >= 0) {
        this.selectedLevels.splice(index, 1);
      } else if (add) {
        this.selectedLevels.push(option);
      }
    }
  },
  mounted: async function () {
    this.logLevel = eventLogger.loglevel;
    await this.reload();
  },
  watch: {
    date() {
      this.reload();
    },
    logLevel() {
      eventLogger.changeLevel(this.logLevel);
      this.showSuccess = true;
    },
    showSuccess(nv) {
      if (nv) {
        clearTimeout(this._ssHandler);
        this._ssHandler = setTimeout(() => {
          this.showSuccess = false;
        }, 1500);
      }
    },
    selectedLevels: function () {
      this.page = 1;
    }
  }
};
</script>
